import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { RootState } from "../../../store";
import { UserActionCreator } from "../../../store/userActions.ts";
import {
  selectClientDealsPending,
  selectClientDealsError,
  selectClientDealsErrorMessage,
  selectClientDealsData,
  selectClientDealsLoadingId,
  selectCreateDealPending,
  selectCreateDealErrorMessage,
  selectUpdateDealPending,
  selectUpdateDealErrorMessage,

  // 👇 статусы сделок
  selectDealStatusesPending,
  selectDealStatusesError,
  selectDealStatusesErrorMessage,
  selectDealStatusCatalog,
} from "../../../store/selectors.ts";

import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import { DealComments } from "../DealComments";
import { ReactComponent as DeleteIcon } from "../../../assets/icons/delete.svg";
import { Button } from "../../../components/common/Button";
import { formatMoney } from "../utils";

type Deal = any;
type FinalValueMode = "calculation" | "manual";

type SharedCalculation = {
  id: number | string;
  code?: string;
  fullPrice?: number | null;
  installmentPeriod?: number | null;
  firstPayment?: number | null;
  monthlyPayment?: number | null;
  content?: any;
};

const getCalculationImage = (calculation: SharedCalculation) =>
    calculation.content?.housingData?.images?.[0] ??
    calculation.content?.images?.[0] ??
    null;

type OwnProps = {
  clientId: number | string;
  isOpen: boolean;
  onDealsChanged?: () => void | Promise<void>;
};

type DealStatusCatalogItem = {
  id: number | string;
  code: string;
  name: string;
  color?: string;
};

type StateProps = {
  dealsPending: boolean;
  dealsError: boolean;
  dealsErrorMessage: string | null;
  deals: Deal[];
  loadingId: number | string | null;

  createPending: boolean;
  createErrorMessage: string | null;

  updatePending: boolean;
  updateErrorMessage: string | null;

  // 👇 статусы
  dealStatusesPending: boolean;
  dealStatusesError: boolean;
  dealStatusesErrorMessage: string | null;
  dealStatusCatalog: DealStatusCatalogItem[];
};

type DispatchProps = {
  getClientDeals: (payload: { clientId: number | string }) => any;
  createClientDeal: (payload: {
    dealStatusId: number;
    clientId: number;
    query: string;
    propertyPrice?: number | null;
  }) => any;
  updateClientDeal: (payload: {
    dealId: number;
    dealStatusId: number;
    query: string;
    propertyPrice?: number | null;
    finalCalculationId?: number | null;
  }) => any;
  deleteDealAttachments: (payload: {
    dealId: number | string;
    attachments: { id: string | number; type: "sharedCalc" | "collection" }[];
  }) => any;
};

type Props = OwnProps & StateProps & DispatchProps;

const _ClientDealsList: React.FC<Props> = ({
                                             clientId,
                                             isOpen,
                                             onDealsChanged,
                                             getClientDeals,
                                             createClientDeal,
                                             updateClientDeal,
                                             deleteDealAttachments,

                                             dealsPending,
                                             dealsError,
                                             dealsErrorMessage,
                                             deals,
                                             loadingId,

                                             createPending,
                                             createErrorMessage,
                                             updatePending,
                                             updateErrorMessage,

                                             dealStatusesPending,
                                             dealStatusesError,
                                             dealStatusesErrorMessage,
                                             dealStatusCatalog,
                                           }) => {
  const [statusId, setStatusId] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const [propertyPrice, setPropertyPrice] = useState("");
  const [finalValueMode, setFinalValueMode] =
      useState<FinalValueMode>("manual");
  const [finalCalculationId, setFinalCalculationId] =
      useState<number | string | null>(null);
  const [finalValueError, setFinalValueError] = useState("");
  const [editingDealId, setEditingDealId] = useState<number | null>(null);

  const mutationPending = createPending || updatePending;
  const mutationErrorMessage = updateErrorMessage || createErrorMessage;

  const isPendingHere = dealsPending && loadingId === clientId;

  const hasStatuses = dealStatusCatalog?.length > 0;

  const defaultStatusId = useMemo<number | null>(() => {
    if (!hasStatuses) return null;
    const first = dealStatusCatalog[0];
    const n = Number(first?.id);
    return Number.isFinite(n) ? n : null;
  }, [dealStatusCatalog, hasStatuses]);

  const selectedStatus = useMemo(
      () =>
          dealStatusCatalog.find(
              (status) => Number(status.id) === Number(statusId),
          ),
      [dealStatusCatalog, statusId],
  );
  const isClosedSuccess = selectedStatus?.code === "closed_success";

  const editingDeal = useMemo(
      () => deals.find((deal) => String(deal.id) === String(editingDealId)),
      [deals, editingDealId],
  );
  const availableCalculations: SharedCalculation[] = useMemo(() => {
    const attachments = editingDeal?.attachments;
    if (!attachments || Array.isArray(attachments)) return [];
    return Array.isArray(attachments.sharedCalc)
        ? attachments.sharedCalc
        : [];
  }, [editingDeal]);
  const selectableCalculations = useMemo(
      () =>
          availableCalculations.filter(
              (calculation) =>
                  calculation.fullPrice != null &&
                  Number(calculation.fullPrice) > 0,
          ),
      [availableCalculations],
  );

  // грузим сделки при первом открытии / смене клиента
  useEffect(() => {
    if (isOpen && !deals?.length && !dealsPending) {
      getClientDeals({ clientId });
    }
  }, [isOpen, clientId]);

  // когда статусы появились — ставим дефолт, если пользователь ещё не выбирал и не редактирует
  useEffect(() => {
    if (!isOpen) return;
    if (editingDealId) return;
    if (statusId != null) return;
    if (defaultStatusId != null) setStatusId(defaultStatusId);
  }, [isOpen, defaultStatusId, statusId, editingDealId]);

  useEffect(() => {
    if (!editingDealId) return;

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;

      setEditingDealId(null);
      setQuery("");
      setPropertyPrice("");
      setFinalValueMode("manual");
      setFinalCalculationId(null);
      setFinalValueError("");
      setStatusId(defaultStatusId);
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editingDealId, defaultStatusId]);

  if (!isOpen) return null;

  const handleRefresh = (e: React.MouseEvent) => {
    e.stopPropagation();
    getClientDeals({ clientId });
  };

  const handleEditClick = (deal: Deal, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDealId(deal.id);

    const sId = Number(deal.dealStatus?.id ?? defaultStatusId);
    setStatusId(Number.isFinite(sId) ? sId : defaultStatusId);

    setQuery(deal.query ?? "");
    const hasFinalCalculation =
        deal.finalCalculationId !== null &&
        deal.finalCalculationId !== undefined;
    setFinalValueMode(hasFinalCalculation ? "calculation" : "manual");
    setFinalCalculationId(
        hasFinalCalculation ? deal.finalCalculationId : null,
    );
    setPropertyPrice(
        hasFinalCalculation ||
        deal.propertyPrice === null ||
        deal.propertyPrice === undefined
            ? ""
            : String(deal.propertyPrice),
    );
    setFinalValueError("");
  };

  const handleCancelEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditingDealId(null);
    setQuery("");
    setPropertyPrice("");
    setFinalValueMode("manual");
    setFinalCalculationId(null);
    setFinalValueError("");
    setStatusId(defaultStatusId);
  };

  const handleStatusChange = (nextStatusId: number) => {
    setStatusId(nextStatusId);
    setFinalValueError("");

    const nextStatus = dealStatusCatalog.find(
        (status) => Number(status.id) === Number(nextStatusId),
    );

    if (nextStatus?.code === "closed_success" && !selectableCalculations.length) {
      setFinalValueMode("manual");
      setFinalCalculationId(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const text = query.trim();
    if (!text) return;

    const currentStatusId = statusId ?? defaultStatusId;
    if (currentStatusId == null) return; // нет статусов — нечего слать
    const closingStatus = dealStatusCatalog.find(
        (status) => Number(status.id) === Number(currentStatusId),
    );
    const isClosingSuccessfully = closingStatus?.code === "closed_success";

    let normalizedPropertyPrice: number | undefined;
    let normalizedFinalCalculationId: number | undefined;

    if (isClosingSuccessfully && finalValueMode === "calculation") {
      if (!finalCalculationId) {
        setFinalValueError("Выберите итоговый расчёт.");
        return;
      }
      normalizedFinalCalculationId = Number(finalCalculationId);
    }

    if (isClosingSuccessfully && finalValueMode === "manual") {
      normalizedPropertyPrice = Number(propertyPrice);
      if (!normalizedPropertyPrice || normalizedPropertyPrice < 1) {
        setFinalValueError("Укажите итоговую сумму сделки.");
        return;
      }
    }

    setFinalValueError("");

    if (editingDealId) {
      const action = await updateClientDeal({
        dealId: editingDealId,
        dealStatusId: Number(currentStatusId),
        query: text,
        propertyPrice: normalizedPropertyPrice,
        finalCalculationId: normalizedFinalCalculationId,
      });
      if (!action?.type?.endsWith("/fulfilled")) return;
      await Promise.all([
        getClientDeals({ clientId }),
        onDealsChanged?.(),
      ]);
    } else {
      const action = await createClientDeal({
        clientId: Number(clientId),
        dealStatusId: Number(currentStatusId),
        query: text,
        propertyPrice: normalizedPropertyPrice,
      });
      if (!action?.type?.endsWith("/fulfilled")) return;
      await Promise.all([
        getClientDeals({ clientId }),
        onDealsChanged?.(),
      ]);
    }

    setQuery("");
    setPropertyPrice("");
    setFinalValueMode("manual");
    setFinalCalculationId(null);
    setFinalValueError("");
    setStatusId(defaultStatusId);
    setEditingDealId(null);
  };

  const handleDeleteAttachment = async (
      dealId: number | string,
      attachmentId: number | string,
      type: "sharedCalc" | "collection",
  ) => {
    try {
      await deleteDealAttachments({
        dealId,
        attachments: [{ id: attachmentId, type }],
      });
      await getClientDeals({ clientId });
    } catch (e) {
      console.error("Не удалось удалить вложение", e);
    }
  };

  return (
      <div className="mt-2 space-y-2 text-xs ml-0 pl-0 border-l-0 sm:ml-4 sm:pl-3 sm:border-l sm:border-gray-200">
        {/* Заголовок + refresh */}
        <div className="flex items-center justify-between mb-1">
          <div className="text-sm font-medium text-gray-800">Сделки клиента</div>

          <button
              onClick={handleRefresh}
              className="p-1 rounded hover:bg-gray-100 transition-colors disabled:opacity-60"
              title="Обновить сделки"
              disabled={isPendingHere}
          >
            <RefreshIcon
                width="16"
                height="16"
                stroke="#818C99"
                className={isPendingHere ? "animate-spin" : ""}
            />
          </button>
        </div>

        {/* состояния статусов (для UX, раз загрузка в родителе) */}
        {dealStatusesPending && (
            <div className="text-gray-500">Загружаем статусы…</div>
        )}
        {!dealStatusesPending && dealStatusesError && (
            <div className="text-red-500">
              Ошибка статусов: {dealStatusesErrorMessage || "Не удалось получить статусы"}
            </div>
        )}

        {/* Список сделок / состояния */}
        {isPendingHere && <div className="text-gray-500">Загружаем сделки…</div>}

        {!isPendingHere && dealsError && (
            <div className="text-red-500">
              Ошибка: {dealsErrorMessage || "Не удалось получить данные"}
            </div>
        )}

        {!isPendingHere && !dealsError && deals?.length === 0 && (
            <div className="text-gray-500">Сделок пока нет</div>
        )}

        {!isPendingHere &&
            !dealsError &&
            deals?.map((d) => {
              const status = d.dealStatus || {};
              const color = status.color || "#9CA3AF";
              const statusName = status.name || "Без статуса";

              const created =
                  d.createdAt &&
                  new Date(d.createdAt).toLocaleString("ru-RU", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  });

              const queryText = d.query || "";
              const isEditingThis = editingDealId === d.id;

              const attachments = d.attachments || {};
              const sharedCalcArray = Array.isArray(attachments)
                  ? attachments
                  : attachments.sharedCalc || [];

              return (
                  <div
                      key={d.id}
                      className={
                          "bg-white border rounded-lg px-3 py-2 space-y-2" +
                          (isEditingThis ? " border-[#c06198]" : "")
                      }
                  >
                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-medium self-start"
                    style={{ backgroundColor: `${color}22`, color }}
                >
                  {statusName}
                </span>

                      <div className="flex items-center justify-between gap-3">
                        {created && (
                            <span className="text-[11px] text-gray-400 whitespace-nowrap">
                      {created}
                    </span>
                        )}
                        <Button
                            type="button"
                            variant="link"
                            text="Редактировать"
                            onClick={(e) => handleEditClick(d, e)}
                            className="text-[11px]"
                        />
                      </div>
                    </div>

                    {queryText && (
                        <div className="text-xs text-gray-700 leading-snug">{queryText}</div>
                    )}

                    {d.propertyPrice !== null && d.propertyPrice !== undefined && (
                        <div className="text-xs text-gray-500">
                          Итоговая сумма сделки:{" "}
                          <span className="font-medium text-gray-700">
                            {formatMoney(d.propertyPrice)}
                          </span>
                        </div>
                    )}

                    {sharedCalcArray.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {sharedCalcArray.map((a: any) => {
                            const imgSrc = getCalculationImage(a);

                            const href = `/shared/housing/${a.code}`;
                            const isFinalCalculation =
                                String(d.finalCalculationId) === String(a.id);

                            return (
                                <div
                                    key={a.id}
                                    className={
                                      "relative rounded-md border p-2 pr-9 " +
                                      (isFinalCalculation
                                          ? "border-emerald-300 bg-emerald-50/50"
                                          : "border-gray-200 bg-gray-50")
                                    }
                                >
                                  <div className="flex items-start gap-3">
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block h-16 w-16 shrink-0 overflow-hidden rounded bg-white"
                                    >
                                      {imgSrc ? (
                                          <img
                                              src={imgSrc}
                                              alt={a.content?.housingData?.housingName || "Расчёт"}
                                              className="h-full w-full object-cover"
                                          />
                                      ) : (
                                          <div className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-gray-500">
                                            Расчёт
                                          </div>
                                      )}
                                    </a>

                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <a
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="font-medium text-gray-800 hover:text-[#c06198]"
                                        >
                                          Расчёт
                                        </a>
                                        {isFinalCalculation && (
                                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700">
                                              Итоговый
                                            </span>
                                        )}
                                      </div>

                                      <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-500 md:grid-cols-4">
                                        <span>
                                          Сумма:{" "}
                                          <strong className="font-medium text-gray-700">
                                            {a.fullPrice == null ? "—" : formatMoney(a.fullPrice)}
                                          </strong>
                                        </span>
                                        <span>
                                          Первый взнос:{" "}
                                          <strong className="font-medium text-gray-700">
                                            {a.firstPayment == null ? "—" : formatMoney(a.firstPayment)}
                                          </strong>
                                        </span>
                                        <span>
                                          Срок:{" "}
                                          <strong className="font-medium text-gray-700">
                                            {a.installmentPeriod == null
                                                ? "—"
                                                : `${a.installmentPeriod} мес.`}
                                          </strong>
                                        </span>
                                        <span>
                                          В месяц:{" "}
                                          <strong className="font-medium text-gray-700">
                                            {a.monthlyPayment == null
                                                ? "—"
                                                : formatMoney(a.monthlyPayment)}
                                          </strong>
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <button
                                      type="button"
                                      onClick={() => handleDeleteAttachment(d.id, a.id, "sharedCalc")}
                                      title="Удалить расчёт"
                                      className="
                            absolute top-2 right-2
                            inline-flex items-center justify-center
                            rounded-full
                            bg-white
                            p-[2px]
                            cursor-pointer
                            border border-gray-200
                          "
                                  >
                                    <DeleteIcon className="w-[18px] h-[18px]" stroke="#818c99" />
                                  </button>
                                </div>
                            );
                          })}
                        </div>
                    )}

                    <DealComments clientId={clientId} dealId={d.id} comments={d.comments} />
                  </div>
              );
            })}

        {editingDealId && (
            <button
                type="button"
                aria-label="Закрыть редактирование сделки"
                className="fixed inset-0 z-[1000] cursor-default bg-black/40"
                onClick={handleCancelEdit}
            />
        )}

        {/* Форма создания / редактирования сделки */}
        <form
            className={
              editingDealId
                  ? "fixed inset-x-0 bottom-0 z-[1001] max-h-[calc(100svh-24px)] space-y-3 overflow-y-auto rounded-t-2xl bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))] shadow-[0_-8px_30px_rgba(0,0,0,0.16)] sm:bottom-auto sm:left-1/2 sm:right-auto sm:top-1/2 sm:w-[min(560px,calc(100vw-32px))] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:p-5"
                  : "mt-3 space-y-2 border-t border-gray-100 pt-2"
            }
            onSubmit={handleSubmit}
            onClick={(e) => e.stopPropagation()}
        >
          {editingDealId && (
              <div className="mb-1 flex items-start justify-between gap-4">
                <div>
                  <div className="text-base font-semibold text-gray-900">
                    Редактирование сделки
                  </div>
                  <div className="mt-0.5 text-[11px] text-gray-500">
                    Измените статус, запрос или итог сделки.
                  </div>
                </div>
                <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="-mr-1 -mt-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xl leading-none text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Закрыть"
                >
                  ×
                </button>
              </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row">
            <select
                className="border rounded px-2 py-1 text-xs sm:w-48 disabled:opacity-60"
                value={statusId ?? ""}
                onChange={(e) => handleStatusChange(Number(e.target.value))}
                disabled={!hasStatuses || dealStatusesPending}
            >
              {!hasStatuses && (
                  <option value="" disabled>
                    {dealStatusesPending ? "Загружаем статусы…" : "Нет статусов"}
                  </option>
              )}

              {dealStatusCatalog.map((s) => (
                  <option key={String(s.id)} value={Number(s.id)}>
                    {s.name}
                  </option>
              ))}
            </select>

            <input
                className="border rounded px-2 py-1 text-xs flex-1 min-w-0"
                placeholder="Запрос / комментарий по сделке"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
            />

          </div>

          {isClosedSuccess && (
              <div className="space-y-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                <div>
                  <div className="text-sm font-medium text-gray-800">Итог сделки</div>
                  <div className="mt-0.5 text-[11px] text-gray-500">
                    Выберите прикреплённый расчёт или укажите сумму вручную.
                  </div>
                </div>

                {editingDealId && selectableCalculations.length > 0 && (
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                          type="button"
                          onClick={() => {
                            setFinalValueMode("calculation");
                            setPropertyPrice("");
                            setFinalValueError("");
                          }}
                          className={
                            "rounded-md border px-3 py-2 text-left text-xs transition-colors " +
                            (finalValueMode === "calculation"
                                ? "border-[#c06198] bg-white text-[#c06198]"
                                : "border-gray-200 bg-white text-gray-600")
                          }
                      >
                        Выбрать расчёт
                      </button>
                      <button
                          type="button"
                          onClick={() => {
                            setFinalValueMode("manual");
                            setFinalCalculationId(null);
                            setFinalValueError("");
                          }}
                          className={
                            "rounded-md border px-3 py-2 text-left text-xs transition-colors " +
                            (finalValueMode === "manual"
                                ? "border-[#c06198] bg-white text-[#c06198]"
                                : "border-gray-200 bg-white text-gray-600")
                          }
                      >
                        Ввести сумму вручную
                      </button>
                    </div>
                )}

                {finalValueMode === "calculation" &&
                    editingDealId &&
                    selectableCalculations.length > 0 && (
                        <div className="space-y-2">
                          {selectableCalculations.map((calculation) => (
                              <label
                                  key={calculation.id}
                                  className={
                                    "flex cursor-pointer items-center gap-3 rounded-lg border bg-white p-2.5 transition-colors " +
                                    (String(finalCalculationId) === String(calculation.id)
                                        ? "border-[#c06198] bg-[#c06198]/[0.04] ring-1 ring-[#c06198]"
                                        : "border-gray-200 hover:border-gray-300")
                                  }
                              >
                                <input
                                    type="radio"
                                    name="finalCalculationId"
                                    value={String(calculation.id)}
                                    checked={
                                      String(finalCalculationId) ===
                                      String(calculation.id)
                                    }
                                    onChange={() => {
                                      setFinalCalculationId(calculation.id);
                                      setFinalValueError("");
                                    }}
                                    className="sr-only"
                                />
                                <span className="block h-16 w-16 shrink-0 overflow-hidden rounded-md bg-gray-100">
                                  {getCalculationImage(calculation) ? (
                                      <img
                                          src={getCalculationImage(calculation)}
                                          alt={
                                            calculation.content?.housingData?.housingName ||
                                            "Объект расчёта"
                                          }
                                          className="h-full w-full object-cover"
                                      />
                                  ) : (
                                      <span className="flex h-full w-full items-center justify-center px-1 text-center text-[10px] text-gray-500">
                                        Без фото
                                      </span>
                                  )}
                                </span>
                                <span className="grid min-w-0 flex-1 grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-gray-600">
                                  <strong className="col-span-2 font-medium text-gray-800">
                                    {calculation.fullPrice == null
                                        ? "Расчёт без указанной суммы"
                                        : formatMoney(calculation.fullPrice)}
                                  </strong>
                                  <span>
                                    Взнос:{" "}
                                    {calculation.firstPayment == null
                                        ? "—"
                                        : formatMoney(calculation.firstPayment)}
                                  </span>
                                  <span>
                                    Срок:{" "}
                                    {calculation.installmentPeriod == null
                                        ? "—"
                                        : `${calculation.installmentPeriod} мес.`}
                                  </span>
                                  <span>
                                    В месяц:{" "}
                                    {calculation.monthlyPayment == null
                                        ? "—"
                                        : formatMoney(calculation.monthlyPayment)}
                                  </span>
                                </span>
                                <span
                                    className={
                                      "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[12px] font-bold " +
                                      (String(finalCalculationId) === String(calculation.id)
                                          ? "border-[#c06198] bg-[#c06198] text-white"
                                          : "border-gray-300 text-transparent")
                                    }
                                    aria-hidden="true"
                                >
                                  ✓
                                </span>
                              </label>
                          ))}
                        </div>
                    )}

                {editingDealId &&
                    availableCalculations.length > 0 &&
                    selectableCalculations.length === 0 && (
                        <div className="text-[11px] text-gray-500">
                          В прикреплённых расчётах нет итоговой суммы, поэтому
                          завершить сделку можно только с ручным вводом.
                        </div>
                    )}

                {finalValueMode === "manual" && (
                    <input
                        className="w-full rounded border px-3 py-2 text-sm sm:max-w-xs"
                        inputMode="numeric"
                        placeholder="Итоговая сумма, ₽"
                        value={
                          propertyPrice
                              ? Number(propertyPrice).toLocaleString("ru-RU")
                              : ""
                        }
                        onChange={(e) => {
                          setPropertyPrice(e.target.value.replace(/\D/g, ""));
                          setFinalValueError("");
                        }}
                    />
                )}

                {!editingDealId && (
                    <div className="text-[11px] text-gray-500">
                      Для новой сделки итоговая сумма указывается вручную.
                    </div>
                )}

                {finalValueError && (
                    <div className="text-[11px] text-red-600" role="alert">
                      {finalValueError}
                    </div>
                )}
              </div>
          )}

          {mutationErrorMessage && (
              <div className="text-[11px] text-red-500">{mutationErrorMessage}</div>
          )}

          <div className="flex items-center gap-2 mt-1">
            <Button
                type="submit"
                text={
                  mutationPending
                      ? "Сохраняем…"
                      : editingDealId
                          ? "Сохранить изменения"
                          : "Добавить сделку"
                }
                pending={mutationPending}
                disabled={
                  !query.trim() ||
                  (statusId == null && defaultStatusId == null) ||
                  (isClosedSuccess &&
                    finalValueMode === "manual" &&
                    !propertyPrice) ||
                  (isClosedSuccess &&
                    finalValueMode === "calculation" &&
                    !finalCalculationId)
                }
                variant="primary"
                className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1 rounded text-xs min-w-0"
            />

            {editingDealId && (
                <button
                    type="button"
                    className="text-[11px] text-gray-500 hover:text-gray-700"
                    onClick={handleCancelEdit}
                >
                  Отмена
                </button>
            )}
          </div>
        </form>
      </div>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => {
  const dealsData = selectClientDealsData(state);

  return {
    dealsPending: selectClientDealsPending(state),
    dealsError: selectClientDealsError(state),
    dealsErrorMessage: selectClientDealsErrorMessage(state),
    loadingId: selectClientDealsLoadingId(state),
    deals: dealsData[ownProps.clientId] ?? [],

    createPending: selectCreateDealPending(state),
    createErrorMessage: selectCreateDealErrorMessage(state),

    updatePending: selectUpdateDealPending(state),
    updateErrorMessage: selectUpdateDealErrorMessage(state),

    dealStatusesPending: selectDealStatusesPending(state),
    dealStatusesError: selectDealStatusesError(state),
    dealStatusesErrorMessage: selectDealStatusesErrorMessage(state),
    dealStatusCatalog: selectDealStatusCatalog(state),
  };
};

const mapDispatchToProps: DispatchProps = {
  getClientDeals: UserActionCreator.getClientDeals as any,
  createClientDeal: UserActionCreator.createClientDeal as any,
  updateClientDeal: UserActionCreator.updateClientDeal as any,
  deleteDealAttachments: UserActionCreator.deleteDealAttachments as any,
};

export const ClientDealsList = connect(
    mapStateToProps,
    mapDispatchToProps,
)(_ClientDealsList);
